import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { isWeb } from '../utilities/platform';
import { API_BASE_URL_CLINIC, CLINIC_STORAGE_KEYS } from '../constants/buyer-portal';
import { ApiError } from '../types';
import { makeApiError } from '../mocks/errors';
import { handleTokenExpired, isInNativeApp } from '../bridge';

export const getClinicToken = (): string | null => {
  if (!isWeb) return null;
  return localStorage.getItem(CLINIC_STORAGE_KEYS.TOKEN);
};

export const setClinicToken = (token: string | null): void => {
  if (!isWeb) return;
  if (token) localStorage.setItem(CLINIC_STORAGE_KEYS.TOKEN, token);
  else localStorage.removeItem(CLINIC_STORAGE_KEYS.TOKEN);
};

export const getRefreshToken = (): string | null => {
  if (!isWeb) return null;
  return localStorage.getItem(CLINIC_STORAGE_KEYS.REFRESH_TOKEN);
};

export const setRefreshToken = (token: string | null): void => {
  if (!isWeb) return;
  if (token) localStorage.setItem(CLINIC_STORAGE_KEYS.REFRESH_TOKEN, token);
  else localStorage.removeItem(CLINIC_STORAGE_KEYS.REFRESH_TOKEN);
};

export const clearAuthTokens = (): void => {
  setClinicToken(null);
  setRefreshToken(null);
};

const mapAxiosError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<Partial<ApiError>>;
    const data = ax.response?.data;
    return makeApiError(
      data?.code ?? 'NETWORK_ERROR',
      data?.message ?? ax.message ?? 'Request failed',
      ax.response?.status,
      data?.details,
    );
  }
  if (err && typeof err === 'object' && 'code' in err && 'message' in err) {
    return err as ApiError;
  }
  return makeApiError('UNKNOWN_ERROR', 'Unknown error');
};

type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

let refreshInFlight: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL_CLINIC}/auth/refresh`,
        { refreshToken },
        { timeout: 20000 },
      );
      const data = res.data as { accessToken?: string; refreshToken?: string };
      if (data.accessToken) setClinicToken(data.accessToken);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      return data.accessToken ?? null;
    } catch {
      clearAuthTokens();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

export const createClinicHttp = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL_CLINIC,
    timeout: 20000,
  });
  instance.interceptors.request.use((config) => {
    const token = getClinicToken();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  });
  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err.config as RetryConfig | undefined;
      const status = err.response?.status;
      // Don't try to refresh on auth endpoints themselves — they don't need a
      // bearer token, and chaining a refresh through a failed /auth/login
      // mutates the in-flight config and leaves axios in a state where the
      // next attempt aborts with ERR_CANCELED.
      const url = original?.url ?? '';
      const isAuthCall =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/logout');
      if (status === 401 && original && !original._retried && !isAuthCall) {
        original._retried = true;
        // Inside the Expo WebView, native owns the token: ask it to refresh.
        // In the browser, fall back to the existing web refresh flow.
        const newToken = isInNativeApp()
          ? await handleTokenExpired(url)
          : await refreshAccessToken();
        if (newToken) {
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
          return instance.request(original);
        }
      }
      return Promise.reject(mapAxiosError(err));
    },
  );
  return instance;
};

export const clinicHttp = createClinicHttp();
